import { createBoard, type Board, type Element, type Piece } from "./board";
import { ELEMENTS, hashSeed, Rng } from "./balance";
import type { BattleProgress, Winner } from "./battle";
import { createOpponentBoard } from "./encounters";
export { createOpponentBoard } from "./encounters";

export type Reward = { element: Element; tier: number };
export type GiftRange = { min: number; max: number };
export type Run = {
  phase: "battle" | "reward" | "board";
  level: number;
  playerBoard: Board;
  opponentBoard: Board;
  rngState: number;
  nextId: number;
  lastResult: Winner | null;
  attempts: number;
  battle?: BattleProgress;
};

export function createRun(element: Element, seed = 2048): Run {
  const playerBoard = createBoard();
  playerBoard.cells[65] = { id: "piece-1", element, tier: 1 };
  return { phase: "battle", level: 1, playerBoard, opponentBoard: createOpponentBoard(1),
    rngState: seed >>> 0, nextId: 2, lastResult: null, attempts: 1, battle: { elapsed: 0, playerInputs: [] } };
}

export function beginBattle(run: Run): Run {
  if (run.phase !== "board" || !run.playerBoard.cells.some(Boolean)) throw new Error("Arrange your team before battle");
  // New fights adopt the current encounter design; saved fights replay their original roster.
  return { ...run, phase: "battle", opponentBoard: createOpponentBoard(run.level), battle: { elapsed: 0, playerInputs: [] } };
}

export function finishBattle(run: Run, winner: Winner, rngState: number): Run {
  if (run.phase !== "battle") throw new Error("This battle has already ended");
  const level = run.level + (winner === "player" ? 1 : 0);
  return { ...run, phase: "reward", battle: undefined, level, lastResult: winner,
    attempts: winner === "player" ? 1 : run.attempts + 1, rngState: rngState >>> 0,
    opponentBoard: winner === "player" ? createOpponentBoard(level) : run.opponentBoard };
}

export function rewardOffers(run: Run, range?: GiftRange): Reward[] {
  const pieces = run.playerBoard.cells.filter((piece): piece is Piece => piece !== null);
  const best = Math.max(1, ...pieces.map(piece => piece.tier)), max = range?.max ?? Math.max(1, best - 1), min = range?.min ?? 1;
  const full = !run.playerBoard.cells.includes(null);
  let eligible = pieces.filter(piece => piece.tier <= max && piece.tier >= min);
  // A full board of only top-rank pieces needs a matching gift to avoid trapping the run.
  if (full && !eligible.length) eligible = pieces;
  return ELEMENTS.flatMap(element => {
    // Stable per gift and element: moving, reopening, and reloads never reroll it.
    const rng = new Rng(hashSeed(`gift:${run.nextId}:${element}`, run.rngState));
    if (!full) {
      return [{ element, tier: min + Math.floor(rng.next() * (max - min + 1)) }];
    }
    const ranks = [...new Set(eligible.filter(piece => piece.element === element).map(piece => piece.tier))].sort((a,b) => a-b);
    if (!ranks.length) return [];
    return [{ element, tier: ranks[Math.floor(rng.next() * ranks.length)] }];
  });
}

export function claimReward(run: Run, reward: Reward, offers = rewardOffers(run), upgradeBest = false): Run {
  if (run.phase !== "reward" || !offers.some((offer) =>
    offer.element === reward.element && offer.tier === reward.tier)) throw new Error("Choose one of the offered elementals");
  const board = run.playerBoard;
  const cells = board.cells.map((piece) => piece ? { ...piece } : null);
  const empty = cells.map((piece, index) => ({ piece, index })).filter(({ piece }) => !piece)
    .sort((a, b) => distance(a.index) - distance(b.index))[0]?.index;
  if (empty !== undefined) {
    cells[empty] = { id: `piece-${run.nextId}`, ...reward };
  } else {
    let index = cells.findIndex((piece) => piece?.element === reward.element && piece.tier === reward.tier);
    const best = Math.max(1, ...cells.map(piece => piece?.tier ?? 1));
    const directUpgrade = index < 0 && upgradeBest && reward.tier === Math.min(51, best + 1);
    if (directUpgrade) index = cells.findIndex(piece => piece?.element === reward.element && piece.tier === best);
    if (index < 0) throw new Error("No space or matching elemental for this reward");
    cells[index] = { ...cells[index]!, tier: directUpgrade ? reward.tier : reward.tier + 1 };
  }
  return { ...run, phase: "board", nextId: run.nextId + 1, playerBoard: { ...board, cells } };
}

function distance(index: number): number {
  return Math.abs(index % 12 - 5) + Math.abs(Math.floor(index / 12) - 5);
}
